import type {} from 'zone.js';

/** Keeps tour listeners cancellable without sharing Zone's passive document listeners. */
export class DriverDocumentEventTarget {
  private readonly callbacks: WeakMap<EventListenerOrEventListenerObject, EventListener> = new WeakMap();
  private readonly nativeAdd: Document['addEventListener'];
  private readonly nativeRemove: Document['removeEventListener'];

  constructor(private readonly target: Document) {
    const add: unknown = typeof Zone === 'undefined' ? null : Reflect.get(target, Zone.__symbol__('addEventListener'));
    const remove: unknown =
      typeof Zone === 'undefined' ? null : Reflect.get(target, Zone.__symbol__('removeEventListener'));
    this.nativeAdd = typeof add === 'function' ? (add as Document['addEventListener']) : target.addEventListener;
    this.nativeRemove =
      typeof remove === 'function' ? (remove as Document['removeEventListener']) : target.removeEventListener;
  }

  /** Registers an independent listener that runs in the caller's original Zone. */
  public addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options: boolean | AddEventListenerOptions = false
  ): void {
    let callback: EventListener | undefined = this.callbacks.get(listener);
    if (callback === undefined) {
      const invoke: EventListener = (event: Event): void => {
        if (typeof listener === 'function') {
          listener.call(this.target, event);
        } else {
          listener.handleEvent(event);
        }
      };
      callback = typeof Zone === 'undefined' ? invoke : Zone.current.wrap(invoke, 'Driver.js document listener');
      this.callbacks.set(listener, callback);
    }

    const listenerOptions: AddEventListenerOptions =
      typeof options === 'boolean' ? { capture: options, passive: false } : { ...options, passive: false };
    this.nativeAdd.call(this.target, type, callback, listenerOptions);
  }

  /** Removes the same native callback registered for this listener and capture phase. */
  public removeEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options: boolean | EventListenerOptions = false
  ): void {
    this.nativeRemove.call(this.target, type, this.callbacks.get(listener) ?? listener, options);
  }
}
